import { Pencil, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { useScenarios } from '@/hooks/useScenarios'
import Button from '@/components/common/Button'

interface BrowseToolbarProps {
  s: ReturnType<typeof useScenarios>
  /** サイドバーで選択中のタブ。表示する新規作成 / 編集の対象を切り替える */
  tab: 'scenarios' | 'routes'
}

// 参照モードのフローティングツールバー（マップ上部中央）。
// サイドバーのタブに応じて、シナリオ / ルートの新規作成・編集をマップ上から行う。
// 編集はサイドバーで対象を1件選択しているときのみ有効。
export default function BrowseToolbar({ s, tab }: BrowseToolbarProps) {
  const { t } = useTranslation()
  const isScenarios = tab === 'scenarios'

  const selectedScenario =
    s.selectedScenarioIds.size === 1
      ? (s.scenarios.find((sc) => s.selectedScenarioIds.has(sc.id)) ?? null)
      : null
  const selectedRoute =
    s.selectedRouteIds.size === 1
      ? (s.routes.find((r) => s.selectedRouteIds.has(r.id)) ?? null)
      : null

  // マップ編集セッションに未保存の編集がある間は、ルート ID の不整合を防ぐため
  // シナリオ / ルートの新規作成・編集へ入れない（保存または破棄してから）
  const createDisabled =
    s.hasPendingMapEdits ||
    (isScenarios
      ? s.isMaxScenariosReached
      : s.routes.length >= s.maxPlaybackRoutes)
  const editTarget = isScenarios ? selectedScenario : selectedRoute
  const editDisabled = !editTarget || s.hasPendingMapEdits

  const handleCreate = () => {
    if (isScenarios) {
      s.handleStartCreate()
    } else {
      s.routeEdit.startCreate()
    }
  }
  const handleEdit = () => {
    if (isScenarios) {
      if (selectedScenario) s.handleStartEdit(selectedScenario)
    } else if (selectedRoute) {
      s.routeEdit.startEdit(selectedRoute)
    }
  }

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-10 flex items-center gap-1.5 bg-white/95 rounded-lg shadow px-2 py-1.5">
      <span className="text-xs font-medium text-gray-500 px-1">
        {isScenarios ? t('scenarios.scenario') : t('scenarios.routes')}
      </span>
      <Button
        onClick={handleCreate}
        disabled={createDisabled}
        variant="blue"
        size="sm"
        className="flex items-center gap-1"
      >
        <Plus size={14} />
        {t('scenarios.create')}
      </Button>
      <Button
        onClick={handleEdit}
        disabled={editDisabled}
        variant="white"
        size="sm"
        className="flex items-center gap-1"
        title={
          isScenarios ? t('scenarios.editTitle') : t('scenarios.routeEditStart')
        }
      >
        <Pencil size={14} />
        {t('scenarios.edit')}
      </Button>
    </div>
  )
}
